// 100%-stacked composition bar + legend — the Inventory Surface's fleet-
// composition look (CompositionBar + toggleable chips) as a kernel primitive.
// Segments are ranked by value and colored by rank from the shared inventory
// palette so the two surfaces read as one visual family.
//
// `splitField` renders one titled bar per distinct value of a column. Each bar
// ranks, colors, folds and totals independently, so a segment's percentage is
// its share of ITS OWN category rather than of the whole result — which is the
// only reading that means anything once the categories are the comparison.

import { useMemo, useState } from "react";
import CompositionBar from "../CompositionBar.jsx";
import { formatCompact } from "../../lib/renderers.js";
import { asNumber, formatValue } from "../../lib/kernelFormat.js";
import { colorForRank } from "../../lib/inventoryDistribution.js";
import { KernelEmpty } from "./shared.jsx";

export const kind = "composition";

// Sentinel key for the folded overflow slice. A literal label collision is
// possible (a provider genuinely called "Other"), so the key carries a prefix
// no dimension value can produce.
const OTHER_KEY = "__other__";
// Neutral grey, deliberately outside the ranked palette so the aggregate never
// reads as one more provider. A literal, NOT a `var(--...)`: CompositionBar
// paints to a canvas, and an unresolvable `fillStyle` is silently ignored by
// the 2D context, leaving the previous segment's color and making "Other"
// duplicate whichever band precedes it.
const OTHER_COLOR = "#9aa3af";

// Bars whose split value is absent from `splitOrder`, and rows whose split
// column is empty, land here.
const UNGROUPED_TITLE = "Other";

/// Rank rows into bar segments, folding everything past `maxItems` into one
/// "Other" slice.
///
/// `share` is computed against the total of EVERY row passed in, not just the
/// ones that survive truncation: this is a 100%-stacked form, so a denominator
/// that drops the tail misstates every segment it does show. Exported for
/// direct unit testing.
export function compositionSegments(data, spec) {
    const arr = Array.isArray(data) ? data : [];
    const ranked = arr
        .map((row) => ({
            key: formatValue(row?.[spec.labelField], "text"),
            label: formatValue(row?.[spec.labelField], "text"),
            count: asNumber(row?.[spec.valueField]) ?? 0,
        }))
        .filter((s) => s.key && s.count > 0)
        // Ties are the common case (every provider reached by one endpoint
        // scores 1); without a label tie-break, which rows fall into "Other"
        // would shuffle between renders.
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    const total = ranked.reduce((n, s) => n + s.count, 0);
    const cap = Math.max(1, spec.maxItems || 12);
    const shown = ranked.slice(0, cap).map((s, i) => ({ ...s, color: colorForRank(i) }));
    const tail = ranked.slice(cap);
    if (tail.length) {
        shown.push({
            key: OTHER_KEY,
            label: "Other",
            count: tail.reduce((n, s) => n + s.count, 0),
            color: OTHER_COLOR,
            otherCount: tail.length,
            // What got folded, so hovering "Other" can name the providers
            // rather than leaving the tail anonymous, which is the whole point
            // of keeping them on the bar.
            members: tail.map((s) => ({
                label: s.label,
                count: s.count,
                share: total ? s.count / total : 0,
            })),
        });
    }
    return shown.map((s) => ({ ...s, share: total ? s.count / total : 0 }));
}

/// One titled bar per distinct `splitField` value, ordered by `splitOrder`
/// then by descending total. Without `splitField` this is a single untitled
/// bar, which is the pre-split behaviour. Exported for direct unit testing.
export function compositionGroups(data, spec) {
    const arr = Array.isArray(data) ? data : [];
    if (!spec.splitField) {
        const segments = compositionSegments(arr, spec);
        return segments.length ? [{ key: "", title: "", segments, scale: 1 }] : [];
    }
    const buckets = new Map();
    for (const row of arr) {
        const raw = formatValue(row?.[spec.splitField], "text");
        const key = raw || UNGROUPED_TITLE;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(row);
    }
    const order = Array.isArray(spec.splitOrder) ? spec.splitOrder : [];
    const rank = (key) => {
        const i = order.indexOf(key);
        // Declared values first in declared order; anything else after.
        return i === -1 ? order.length : i;
    };
    const groups = [...buckets.entries()]
        .map(([key, rows]) => {
            const groupColor = spec.splitColors?.[key];
            const groupPalette = spec.splitPalettes?.[key];
            const segments = compositionSegments(rows, spec).map((segment, index) => {
                if (groupColor) return { ...segment, color: groupColor };
                if (segment.key !== OTHER_KEY && Array.isArray(groupPalette) && groupPalette.length) {
                    return { ...segment, color: groupPalette[index % groupPalette.length] };
                }
                return segment;
            });
            return {
                key,
                title: key.charAt(0).toUpperCase() + key.slice(1),
                segments,
                itemCount: rows.length,
                total: segments.reduce((n, s) => n + s.count, 0),
            };
        })
        .filter((g) => g.segments.length)
        .sort((a, b) => rank(a.key) - rank(b.key) || b.total - a.total || a.key.localeCompare(b.key));
    const peak = Math.max(0, ...groups.map((group) => group.total));
    return groups.map((group) => ({
        ...group,
        scale: spec.sharedScale && peak ? group.total / peak : 1,
    }));
}

export function Component({ spec, data }) {
    const groups = useMemo(
        () => compositionGroups(data, spec),
        [
            data,
            spec.labelField,
            spec.valueField,
            spec.splitField,
            spec.splitOrder,
            spec.splitColors,
            spec.splitPalettes,
            spec.sharedScale,
            spec.maxItems,
        ],
    );

    // Keyed by `${group}::${segment}` so hiding a provider in one category
    // does not hide the same provider in the other.
    const [hidden, setHidden] = useState(() => new Set());
    if (!groups.length) return <KernelEmpty message={spec.empty || "no rows"} />;

    const toggle = (key) => setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    return (
        <div className="flex flex-col gap-[10px]">
            {groups.map((group) => {
                const hiddenKey = (s) => `${group.key}::${s.key}`;
                const visible = group.segments.filter((s) => !hidden.has(hiddenKey(s)));
                const anyHidden = group.segments.some((s) => hidden.has(hiddenKey(s)));
                return (
                    <div key={group.key || "all"}>
                        {group.title ? (
                            <div className="mb-[3px] flex items-baseline gap-[6px]">
                                <span className="text-[10px] font-medium uppercase tracking-[0.07em] text-[color:var(--muted)]">
                                    {group.title}
                                </span>
                                <span className="text-[9px] tabular-nums text-[color:var(--muted-soft)]">
                                    {group.itemCount} {group.itemCount === 1 ? "provider" : "providers"}
                                    {spec.valueLabel
                                        ? `, ${formatCompact(group.total)} ${spec.valueLabel}`
                                        : ""}
                                </span>
                            </div>
                        ) : null}
                        <div className={spec.sharedScale
                            ? "h-[34px] rounded-[2px] bg-[color:var(--panel-soft)]"
                            : "h-[34px]"}
                        >
                            <div
                                className="h-full"
                                style={spec.sharedScale ? { width: `${Math.max(0, group.scale) * 100}%` } : undefined}
                            >
                                <CompositionBar segments={visible} />
                            </div>
                        </div>
                        <div className="mt-[4px] flex flex-wrap items-center gap-x-[2px] gap-y-[1px]">
                            {group.segments.map((s) => {
                                const on = !hidden.has(hiddenKey(s));
                                return (
                                    <button
                                        key={s.key}
                                        type="button"
                                        onClick={() => toggle(hiddenKey(s))}
                                        aria-label={on ? `Hide ${s.label}` : `Show ${s.label}`}
                                        title={
                                            s.members?.length
                                                ? `${s.otherCount} more (${Math.round(s.share * 100)}%): ${s.members
                                                    .map((m) => `${m.label} ${formatCompact(m.count)} (${Math.round(m.share * 100)}%)`)
                                                    .join(", ")}`
                                                : `${s.label}: ${formatCompact(s.count)} (${Math.round(s.share * 100)}%)`
                                        }
                                        className={[
                                            "inline-flex items-center gap-[3px] rounded-[2px] border border-transparent px-[5px] py-[2px] text-[9px] leading-[1.08] transition-colors cursor-pointer",
                                            on
                                                ? "text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                                                : "text-[color:var(--muted-soft)] line-through opacity-50 hover:opacity-100 hover:text-[color:var(--muted)]",
                                        ].join(" ")}
                                    >
                                        <span
                                            className={["h-[5px] w-[5px] rounded-[999px]", on ? "" : "ring-1 ring-inset ring-[color:var(--line-strong)]"].join(" ")}
                                            style={{ backgroundColor: on ? s.color : "transparent" }}
                                            aria-hidden="true"
                                        />
                                        {s.label}
                                        <span className="tabular-nums text-[color:var(--muted-soft)]">{formatCompact(s.count)}</span>
                                    </button>
                                );
                            })}
                            {anyHidden ? (
                                <button
                                    type="button"
                                    onClick={() => setHidden((prev) => {
                                        const next = new Set(prev);
                                        for (const s of group.segments) next.delete(hiddenKey(s));
                                        return next;
                                    })}
                                    aria-label="Show all segments"
                                    className="ml-[2px] inline-flex items-center rounded-[2px] border border-[color:var(--line)] px-[5px] py-[2px] text-[9px] leading-[1.08] tracking-[0.06em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
                                >
                                    All
                                </button>
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
